import { DndContext } from "@dnd-kit/core";
import { Button } from "components/Button";
import React, { useEffect, useState } from "react";
import { Draggable } from "./Draggable";
import { Droppable } from "./Droppable";

interface DraggableModalProps {
  children: React.ReactNode;
  title?: string;
  isOpen: boolean | undefined;
  setIsOpen: (isOpen: boolean) => void;
  onSave: () => void;
  onCancel?: () => void;
  id: string;
  initialPosition: { x: number; y: number };
  validation: (note: any) => boolean;
}

const DraggableModal: React.FC<DraggableModalProps> = ({
  children,
  title = "Modal",
  id = "draggableModal",
  isOpen,
  setIsOpen,
  onSave,
  onCancel,
  initialPosition,
  validation,
}) => {
  const [note, setNote] = useState({
    id: id,
    content: children,
    position: initialPosition,
  });

  // if the user changes the document zoom update the position of the note
  useEffect(() => {
    const handleResize = () => {
      if (note) {
        const parentDiv = document.getElementById("discrepancy-grid");
        const viewportWidth = parentDiv
          ? parentDiv.offsetWidth
          : window.innerWidth;
        const viewportHeight = parentDiv
          ? parentDiv.offsetHeight
          : window.innerHeight;

        const maxX = viewportWidth - 440;
        const maxY = viewportHeight - 150;

        if (note.position.x > maxX) {
          note.position.x = maxX;
        }

        if (note.position.y > maxY) {
          note.position.y = maxY;
        }

        setNote({ ...note });
      }
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [note]);

  function handleDragEnd(ev) {
    if (!note) return;

    const parentDiv = document.getElementById("discrepancy-grid");
    const viewportWidth = parentDiv ? parentDiv.offsetWidth : window.innerWidth;
    const viewportHeight = parentDiv
      ? parentDiv.offsetHeight
      : window.innerHeight;

    const maxX = viewportWidth - 440;
    const maxY = viewportHeight - 150;

    if (note.position.x + ev.delta.x < 0) {
      ev.delta.x = -note.position.x;
    }

    if (note.position.x + ev.delta.x > maxX) {
      ev.delta.x = maxX - note.position.x;
    }

    if (note.position.y + ev.delta.y < 0) {
      ev.delta.y = -note.position.y;
    }

    if (note.position.y + ev.delta.y > maxY) {
      ev.delta.y = maxY - note.position.y;
    }

    const updatedNote = {
      ...note,
      position: {
        x: note.position.x + ev.delta.x,
        y: note.position.y + ev.delta.y,
      },
    };
    setNote(updatedNote);
  }

  if (!isOpen) {
    return null;
  }

  return (
    <DndContext onDragEnd={handleDragEnd}>
      <Droppable>
        <Draggable
          styles={{
            position: "absolute",
            left: `${note.position.x}px`,
            top: `${note.position.y}px`,
            display: "flex",
          }}
          title={title}
          id={note.id}
          content={
            <>
              {note.content}
              <div
                slot="footer-end"
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "8px",
                }}
              >
                <Button
                  type="button"
                  scale="m"
                  onClick={() => {
                    setIsOpen(false);
                    if (onCancel) onCancel();
                  }}
                  variant="outline"
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  scale="m"
                  onClick={onSave}
                  disabled={!validation(note)}
                >
                  Save
                </Button>
              </div>
            </>
          }
        />
      </Droppable>
    </DndContext>
  );
};

export default DraggableModal;
